import React from 'react';
import { Card, Table, Tag, Select, Alert, Typography, Button, Row, Col } from 'antd';
import { FIELD_DEFS, OUTPUT_RELEVANT_FIELDS } from '../../utils/headerAliases';

const { Option } = Select;
const { Text } = Typography;

// Source-only fields that aren't template columns but are still mappable, with their labels.
// Model No feeds the auto-created Model (Make + Sub Type + Model No); the identifiers feed
// duplicate detection. None of these are exported as their own column.
const SOURCE_LABELS = {
  modelNo: 'Model No (builds Model)',
  altName: 'Registration / Alt Name (for dedup)',
  serial: 'Chassis / Serial No (for dedup)',
  existingAssetCode: 'Existing Asset Code (for dedup)',
  remark: 'Remark (Description fallback)',
};

// Human labels for canonical fields (falls back to the source label, then the key).
const fieldLabel = (fk) => (FIELD_DEFS[fk] ? FIELD_DEFS[fk].outputColumn.trim() : (SOURCE_LABELS[fk] || fk));

// Fields offered in the manual-map dropdown: every template field that is mapped from source
// (auto-generated columns like Model / Location Code are excluded), plus the source-only
// helper fields above.
const MAPPABLE_FIELDS = [
  ...Object.keys(FIELD_DEFS).filter((fk) => !FIELD_DEFS[fk].autoGenerated),
  ...Object.keys(SOURCE_LABELS),
];

/**
 * Step 2 UI: shows the auto-detected column → field mapping so the user can confirm it,
 * fix any wrong auto-map, and assign any unmapped source columns. No mapping is hidden;
 * nothing is fuzzy — unknown headers simply appear here for a one-time decision.
 *
 * Props:
 *  - headers: [{index, raw}]
 *  - mapping: fieldKey -> { sourceIndex, sourceHeader, via, confidence }
 *  - unmappedSourceColumns: [{index, header}]
 *  - unmappedRequiredFields: string[]
 *  - headerAmbiguous: bool, headerRowIndex: number
 *  - droppedEmptyColumns: [{index, raw}]
 *  - onSetOverride(fieldKey, colIndexOrNull)
 *  - onBack(), onConfirm()
 */
function ColumnMappingReview(props) {
  const {
    headers = [], mapping = {}, unmappedSourceColumns = [], unmappedRequiredFields = [],
    headerAmbiguous, headerRowIndex, droppedEmptyColumns = [],
    onSetOverride, onBack, onConfirm,
  } = props;

  // Output-relevant mappings go in the main table; source-only helpers (dedup / Description
  // fallback) are listed separately and quietly so they don't clutter the output mapping.
  const toRow = (fk) => ({
    key: fk,
    fieldKey: fk,
    field: fieldLabel(fk),
    sourceHeader: mapping[fk].sourceHeader,
    via: mapping[fk].via,
    sourceIndex: mapping[fk].sourceIndex,
  });
  const mappedRows = Object.keys(mapping).filter((fk) => OUTPUT_RELEVANT_FIELDS.includes(fk)).map(toRow);
  const helperRows = Object.keys(mapping).filter((fk) => !OUTPUT_RELEVANT_FIELDS.includes(fk)).map(toRow);

  const viaTag = (via) => {
    const map = { alias: ['green', 'auto'], learned: ['blue', 'learned'], manual: ['gold', 'manual'] };
    const [color, label] = map[via] || ['default', via];
    return <Tag color={color}>{label}</Tag>;
  };

  return (
    <div>
      {headerAmbiguous && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Header row detection was not confident"
          description={`We treated row ${headerRowIndex + 1} as the header. If that's wrong, the column mapping below will look off — go Back and check the file.`}
        />
      )}
      {unmappedRequiredFields.length > 0 && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="Some required fields are not mapped yet"
          description={`Unmapped required: ${unmappedRequiredFields.map(fieldLabel).join(', ')}. Map them from the unmapped source columns below, or they'll be flagged for review per row.`}
        />
      )}
      {droppedEmptyColumns.length > 0 && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={`${droppedEmptyColumns.length} empty placeholder column(s) ignored`}
          description={droppedEmptyColumns.map((c) => c.raw || `(col ${c.index + 1})`).join(', ')}
        />
      )}

      <Card title="Auto-mapped columns" size="small" style={{ marginBottom: 16 }}>
        <Table
          dataSource={mappedRows}
          rowKey="key"
          pagination={false}
          size="small"
          columns={[
            { title: 'AssetPro Field', dataIndex: 'field', key: 'field', width: 200,
              render: (t, r) => <span>{t}{FIELD_DEFS[r.fieldKey] && FIELD_DEFS[r.fieldKey].required && <span style={{ color: 'red' }}> *</span>}</span> },
            { title: 'Mapped from (your file)', dataIndex: 'sourceHeader', key: 'sourceHeader',
              render: (t) => <Text strong>{t}</Text> },
            { title: 'How', dataIndex: 'via', key: 'via', width: 100, render: viaTag },
            { title: '', key: 'clear', width: 90,
              render: (_, r) => <Button size="small" onClick={() => onSetOverride(r.fieldKey, null)}>Unmap</Button> },
          ]}
        />
        {helperRows.length > 0 && (
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #f0f0f0' }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Also detected (not exported as columns — used only for calculations):{' '}
              {helperRows.map((r, i) => (
                <span key={r.fieldKey}>
                  {i > 0 ? ', ' : ''}<Text strong style={{ fontSize: 12 }}>{r.sourceHeader}</Text> → {fieldLabel(r.fieldKey)}
                </span>
              ))}
            </Text>
          </div>
        )}
      </Card>

      <Card title="Unmapped columns from your file" size="small" style={{ marginBottom: 16 }}>
        {unmappedSourceColumns.length === 0 ? (
          <Text type="secondary">All columns were recognized.</Text>
        ) : (
          <Table
            dataSource={unmappedSourceColumns.map((c) => ({ key: c.index, ...c }))}
            rowKey="key"
            pagination={false}
            size="small"
            columns={[
              { title: 'Column in your file', dataIndex: 'header', key: 'header',
                render: (t, r) => <Text strong>{t || `(column ${r.index + 1})`}</Text> },
              { title: 'Map to AssetPro field', key: 'assign',
                render: (_, r) => (
                  <Select
                    style={{ width: 260 }}
                    placeholder="Leave unmapped (won't be imported)"
                    allowClear
                    showSearch
                    optionFilterProp="children"
                    onChange={(val) => onSetOverride(val, r.index)}
                  >
                    {MAPPABLE_FIELDS.map((fk) => (
                      <Option key={fk} value={fk}>{fieldLabel(fk)}</Option>
                    ))}
                  </Select>
                ) },
            ]}
          />
        )}
      </Card>

      <Row gutter={16}>
        <Col><Button onClick={onBack}>Back</Button></Col>
        <Col><Button type="primary" onClick={onConfirm}>Continue to review</Button></Col>
      </Row>
    </div>
  );
}

export default ColumnMappingReview;
